import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  UncontrolledTooltip,
  Alert,
  Collapse,
  Progress
} from 'reactstrap';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import JSZip from 'jszip';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, convertToRaw, convertFromRaw, ContentState } from 'draft-js';
import {
  fetchFullLengthExamsCached,
  fetchFullLengthExam,
  rebuildExamCache,
  updateFullLengthExam,
  deleteFullLengthExam,
  archiveFullLengthExam
} from '../../../redux/QuestionBank/FullLengthExamAction';
import { generateBatchId, uploadBatchFile, saveUploadLog } from '../../../redux/QuestionBank/UploadLogsAction';
import UploadHistorySection from '../../../components/QuestionBank/UploadHistorySection';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchQuestions } from '../../../redux/QuestionBank/QuestionsAction';
import { draftRawToPlainText, textToDraftRaw, textToDraftRawWithImage, renderDraftContent } from '../../../util/draftToHtml';
import ResizableImage from '../../../components/QuestionBank/ResizableImage';
import { uploadImage } from '../../../util/imageUpload';
import { optimizeImage } from '../../../util/imageOptimize';
import { getSpecialtyStorageFolder } from '../../../util/specialtyCollections';
import { resolveSubject, resolveUnit, resolveTopic } from '../../../util/resolveSubjectUnitTopic';
import {
  questionTextToolbar,
  choiceToolbar,
  explanationToolbar,
  editorContainerStyles
} from '../../../util/editorToolbarConfig';
import { getSpecialty } from 'config/specialtyConfig';
import { getDisplayName } from 'util/specialtyDisplayHelper';

// Dynamic choice support constants
const MAX_CHOICES = 10;
const CHOICE_LABELS = Array.from({ length: MAX_CHOICES }, (_, i) => String.fromCharCode(65 + i));
const CORRECT_ANSWER_MAP = Object.fromEntries(CHOICE_LABELS.map((label, i) => [label, i.toString()]));
const ID_TO_LABEL_MAP = Object.fromEntries(CHOICE_LABELS.map((label, i) => [i.toString(), label]));
const ANSWER_DROPDOWN_STRING = CHOICE_LABELS.join(',');
const FIRST_CHOICE_COL = 4;
const EXPLANATION_COL = FIRST_CHOICE_COL + MAX_CHOICES + 1; // 15
const QUESTION_IMAGE_COL = EXPLANATION_COL + 1; // 16
const QUESTION_IMAGE_WIDTH_COL = QUESTION_IMAGE_COL + 1; // 17
const FIRST_CHOICE_IMAGE_COL = QUESTION_IMAGE_WIDTH_COL + 1; // 18, stride 2
const EXPLANATION_IMAGE_COL = FIRST_CHOICE_IMAGE_COL + MAX_CHOICES * 2; // 38
const EXPLANATION_IMAGE_WIDTH_COL = EXPLANATION_IMAGE_COL + 1; // 39
const SUPPORTED_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'];

const ManageFullLengthExamsPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { exams, examsLoading, updateLoading, deleteLoading, attemptCounts, attemptCountsLoading } = useSelector(
    state => state.fullLengthExams || { exams: [], examsLoading: false, attemptCounts: {} }
  );
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { questions } = useSelector(state => state.questionBankQuestions || { questions: [] });
  const userHandle = useSelector(state => state.loginAuth?.userDetails?.handle);
  const activeSpecialty = useSelector(state => state.specialty.activeSpecialty);

  const [searchTerm, setSearchTerm] = useState('');

  // Captured re-upload file for logging
  const [reUploadFile, setReUploadFile] = useState(null);

  // Edit/View modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', isActive: true });
  const [editExamLoading, setEditExamLoading] = useState(false);

  // Review/edit questions state
  const [editQuestions, setEditQuestions] = useState([]);
  const [editingQuestionIndex, setEditingQuestionIndex] = useState(null);
  const [questionSearchTerm, setQuestionSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 25;

  // Deep link (?examId=...&questionId=...) — e.g. from exam feedback
  const [pendingQuestionId, setPendingQuestionId] = useState(null);
  const deepLinkHandledRef = useRef(false);

  // Lazy WYSIWYG editor states
  const [editorStates, setEditorStates] = useState({});

  // Image/ZIP state
  const [imageFiles, setImageFiles] = useState({});
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);

  // Load exams on mount
  useEffect(() => {
    dispatch(fetchFullLengthExamsCached());
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchQuestions({ pageSize: 5000 }));
  }, [dispatch, activeSpecialty]);

  const [isRebuildingCache, setIsRebuildingCache] = useState(false);
  const handleRebuildCache = async () => {
    setIsRebuildingCache(true);
    await dispatch(rebuildExamCache());
    setIsRebuildingCache(false);
  };

  // Detect exams that contain archived (missing) questions
  const activeQuestionIds = useMemo(() => {
    return new Set((questions || []).map(q => q.questionId));
  }, [questions]);

  const getArchivedCount = (exam) => {
    if (exam.questions || exam.hasEmbeddedQuestions) return 0; // Embedded questions exam
    const ids = exam.questionIds || [];
    if (!ids.length || !activeQuestionIds.size) return 0;
    return ids.filter(id => !activeQuestionIds.has(id)).length;
  };

  // Get subject/unit names
  const getSubjectName = (subjectId) => {
    const subject = subjects?.find(s => s.subjectId === subjectId);
    return subject?.name || subjectId;
  };

  const getUnitName = (unitId) => {
    const unit = units?.find(u => u.unitId === unitId);
    return unit?.name || unitId;
  };

  // Helper: sanitize name for Excel named range
  const sanitizeNameForExcel = (name) => {
    return 'R_' + name
      .replace(/[^a-zA-Z0-9]/g, '_')
      .replace(/^[0-9]/, 'N$&')
      .substring(0, 250);
  };

  // Download Excel template with cascading dropdowns
  const downloadTemplate = async () => {
    if (!subjects || subjects.length === 0) {
      Swal.fire('No Data', 'Please wait for subjects to load before downloading the template.', 'warning');
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = getSpecialty(activeSpecialty).name + ' Prep';
      workbook.created = new Date();

      const subjectsList = subjects.map(s => ({
        name: s.name, code: s.code || '',
        displayName: getDisplayName(s, activeSpecialty)
      })).filter(s => s.name).sort((a, b) => a.name.localeCompare(b.name));
      const subjectDisplayNames = subjectsList.map(s => s.displayName);

      const unitsList = (units || []).map(u => {
        const subject = subjects.find(s => s.subjectId === u.subjectId);
        return {
          name: u.name, code: u.code || '',
          displayName: getDisplayName(u, activeSpecialty),
          subjectDisplayName: getDisplayName(subject, activeSpecialty)
        };
      }).filter(u => u.name);

      const topicsList = (topics || []).map(t => {
        const unit = (units || []).find(u => u.unitId === t.unitId);
        return {
          name: t.name, code: t.code || '',
          displayName: getDisplayName(t, activeSpecialty),
          unitDisplayName: getDisplayName(unit, activeSpecialty)
        };
      }).filter(t => t.name);

      const unitsBySubject = {};
      unitsList.forEach(u => {
        if (!unitsBySubject[u.subjectDisplayName]) unitsBySubject[u.subjectDisplayName] = [];
        unitsBySubject[u.subjectDisplayName].push(u.displayName);
      });

      const topicsByUnit = {};
      topicsList.forEach(t => {
        if (!topicsByUnit[t.unitDisplayName]) topicsByUnit[t.unitDisplayName] = [];
        topicsByUnit[t.unitDisplayName].push(t.displayName);
      });

      // Hidden Lists sheet
      const listsSheet = workbook.addWorksheet('Lists');
      listsSheet.state = 'veryHidden';

      let currentCol = 1;
      subjectDisplayNames.forEach((displayName, i) => {
        listsSheet.getCell(i + 1, currentCol).value = displayName;
      });
      workbook.definedNames.add(`Lists!$A$1:$A$${subjectDisplayNames.length}`, 'SubjectsList');

      currentCol = 2;
      Object.keys(unitsBySubject).sort().forEach(subjectDisplayName => {
        const unitsForSubject = unitsBySubject[subjectDisplayName];
        unitsForSubject.forEach((unitDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = unitDisplayName;
        });
        const rangeName = sanitizeNameForExcel(subjectDisplayName);
        const colLetter = String.fromCharCode(64 + currentCol);
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${unitsForSubject.length}`, rangeName);
        currentCol++;
      });

      Object.keys(topicsByUnit).sort().forEach(unitDisplayName => {
        const topicsForUnit = topicsByUnit[unitDisplayName];
        topicsForUnit.forEach((topicDisplayName, i) => {
          listsSheet.getCell(i + 1, currentCol).value = topicDisplayName;
        });
        const rangeName = sanitizeNameForExcel('T_' + unitDisplayName);
        let colLetter;
        if (currentCol <= 26) {
          colLetter = String.fromCharCode(64 + currentCol);
        } else {
          const first = Math.floor((currentCol - 1) / 26);
          const second = ((currentCol - 1) % 26) + 1;
          colLetter = String.fromCharCode(64 + first) + String.fromCharCode(64 + second);
        }
        workbook.definedNames.add(`Lists!$${colLetter}$1:$${colLetter}$${topicsForUnit.length}`, rangeName);
        currentCol++;
      });

      // Reference Data sheet
      const refSheet = workbook.addWorksheet('Reference_Data');
      refSheet.getRow(1).values = ['Subject', 'Subject Code', 'Unit', 'Unit Code', 'Topic', 'Topic Code'];
      refSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      refSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } };
      const sortedUnits = [...unitsList].sort((a, b) => a.name.localeCompare(b.name));
      const sortedTopics = [...topicsList].sort((a, b) => a.name.localeCompare(b.name));
      const maxRows = Math.max(subjectsList.length, sortedUnits.length, sortedTopics.length);
      for (let i = 0; i < maxRows; i++) {
        refSheet.getRow(i + 2).values = [
          subjectsList[i]?.name || '', subjectsList[i]?.code || '',
          sortedUnits[i]?.name || '', sortedUnits[i]?.code || '',
          sortedTopics[i]?.name || '', sortedTopics[i]?.code || ''
        ];
      }
      refSheet.columns = [{ width: 25 }, { width: 20 }, { width: 30 }, { width: 20 }, { width: 25 }, { width: 20 }];

      // Questions Template sheet
      const questionsSheet = workbook.addWorksheet('Questions Template');
      const templateColumns = [
        'Subject Name', 'Unit Name', 'Topic Name (Optional)',
        'Question Text', ...CHOICE_LABELS.map(l => `Choice ${l}`),
        'Correct Answer', 'Explanation',
        'Question Image (filename)',
        'Question Image Width (px)',
        ...CHOICE_LABELS.flatMap(l => [`Choice ${l} Image (filename)`, `Choice ${l} Image Width (px)`]),
        'Explanation Image (filename)',
        'Explanation Image Width (px)'
      ];
      const imageColStart = templateColumns.indexOf('Question Image (filename)');
      questionsSheet.getRow(1).values = templateColumns;
      questionsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      questionsSheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF5E72E4' } };

      // Style image columns with orange header
      const tplHeaderRow = questionsSheet.getRow(1);
      for (let ci = imageColStart; ci < templateColumns.length; ci++) {
        const cell = tplHeaderRow.getCell(ci + 1);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFED7D31' } };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      }

      questionsSheet.columns = [
        { width: 22 }, { width: 28 }, { width: 22 },
        { width: 60 }, ...CHOICE_LABELS.map(() => ({ width: 40 })),
        { width: 15 }, { width: 60 },
        { width: 28 }, { width: 18 },  // Question Image + Width
        ...CHOICE_LABELS.flatMap(() => [{ width: 22 }, { width: 18 }]),  // Choice A-J Image + Width
        { width: 28 }, { width: 18 }   // Explanation Image + Width
      ];

      for (let i = 2; i <= 21; i++) {
        questionsSheet.getRow(i).getCell(1).value = subjectDisplayNames[0] || '';
        questionsSheet.getRow(i).getCell(FIRST_CHOICE_COL + MAX_CHOICES + 1).value = 'A';
      }

      for (let rowNum = 2; rowNum <= 201; rowNum++) {
        questionsSheet.getCell(`A${rowNum}`).dataValidation = {
          type: 'list', allowBlank: false, formulae: ['SubjectsList'],
          showErrorMessage: true, errorTitle: 'Invalid Subject', error: 'Please select a subject from the dropdown list'
        };
        questionsSheet.getCell(`B${rowNum}`).dataValidation = {
          type: 'list', allowBlank: false,
          formulae: [`INDIRECT("R_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(A${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
          showErrorMessage: true, errorTitle: 'Invalid Unit', error: 'Please select a unit. Select a Subject first!'
        };
        if (Object.keys(topicsByUnit).length > 0) {
          questionsSheet.getCell(`C${rowNum}`).dataValidation = {
            type: 'list', allowBlank: true,
            formulae: [`INDIRECT("R_T_"&SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(SUBSTITUTE(B${rowNum}," ","_"),"-","_"),"/","_"),"(","_"),")","_"),"&","_"),".","_"),",","_"))`],
            showErrorMessage: true, errorTitle: 'Invalid Topic', error: 'Please select a topic or leave blank. Select a Unit first!'
          };
        }
        const correctAnswerCol = String.fromCharCode(65 + FIRST_CHOICE_COL + MAX_CHOICES);
        questionsSheet.getCell(`${correctAnswerCol}${rowNum}`).dataValidation = {
          type: 'list', allowBlank: false, formulae: [`"${ANSWER_DROPDOWN_STRING}"`],
          showErrorMessage: true, errorTitle: 'Invalid Answer', error: `Please select ${ANSWER_DROPDOWN_STRING}`
        };
      }
      questionsSheet.views = [{ state: 'frozen', ySplit: 1 }];

      // Instructions sheet
      const instructionsSheet = workbook.addWorksheet('Instructions');
      const instructions = [
        ['Full-Length Exam Upload Template'],
        [''],
        ['HOW TO USE THIS TEMPLATE:'],
        [''],
        ['1. Go to the "Questions Template" sheet to enter your questions'],
        ['2. Select a SUBJECT first - this will filter the Unit dropdown!'],
        ['3. Select a UNIT - this will filter the Topic dropdown!'],
        ['4. Fill in the Question, Choices (A and B required, C-J optional), Correct Answer, and Explanation'],
        [''],
        ['CASCADING DROPDOWNS:'],
        ['Subject dropdown shows all available subjects'],
        ['Unit dropdown automatically shows only units for the selected subject'],
        ['Topic dropdown automatically shows only topics for the selected unit'],
        [''],
        ['Column Descriptions:'],
        ['- Subject Name: Select from dropdown (required)'],
        ['- Unit Name: Auto-filtered by Subject (required)'],
        ['- Topic Name: Auto-filtered by Unit (optional)'],
        ['- Question Text: The question stem (required)'],
        ['- Choice A-J: Answer options (A and B required, C-J optional)'],
        [`- Correct Answer: Select ${ANSWER_DROPDOWN_STRING} (required)`],
        ['- Explanation: Explanation shown after answering (recommended)'],
        ['- Question Image / Choice Image / Explanation Image: Filename of an image in the ZIP (optional)'],
        [''],
        ['IMAGE SUPPORT (Optional):'],
        ['To add images to questions, choices, or explanations:'],
        ['1. Create a ZIP file containing your Excel file and an /images folder'],
        ['2. In the image columns, enter just the filename (e.g., "diagram1.png")'],
        ['3. Place the corresponding image files in the /images folder inside the ZIP'],
        ['4. Upload the .zip file instead of the .xlsx file'],
        [''],
        ['Supported image formats: .png, .jpg, .jpeg, .gif, .webp, .svg'],
        [''],
        ['NOTE: First 20 rows are pre-filled with defaults. You can add more rows (up to 200).'],
      ];
      instructions.forEach((row, index) => {
        const excelRow = instructionsSheet.getRow(index + 1);
        excelRow.values = row;
        if (index === 0) excelRow.font = { bold: true, size: 16, color: { argb: 'FF5E72E4' } };
        else if (row[0]?.startsWith('HOW TO USE') || row[0]?.startsWith('CASCADING') || row[0]?.startsWith('IMAGE SUPPORT')) excelRow.font = { bold: true, size: 12 };
      });
      instructionsSheet.getColumn(1).width = 80;

      const sheetsOrder = ['Instructions', 'Questions Template', 'Reference_Data', 'Lists'];
      workbook.eachSheet((sheet) => {
        const index = sheetsOrder.indexOf(sheet.name);
        if (index !== -1) sheet.orderNo = index;
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Full_Length_Exam_Template.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Template Downloaded!',
        html: `
          <p>The template has <strong>cascading dropdowns</strong>:</p>
          <ul style="text-align: left;">
            <li><strong>Subject</strong> → Select first (${subjectDisplayNames.length} options)</li>
            <li><strong>Unit</strong> → Auto-filters by Subject!</li>
            <li><strong>Topic</strong> → Auto-filters by Unit!</li>
            <li><strong>Correct Answer</strong> → ${ANSWER_DROPDOWN_STRING}</li>
          </ul>
          <p><strong>20 rows pre-filled</strong> with defaults. Add more as needed (up to 200).</p>
        `,
        confirmButtonText: 'Got it!',
        confirmButtonColor: '#5e72e4'
      });
    } catch (error) {
      console.error('Error generating template:', error);
      Swal.fire('Error', 'Failed to generate template. Please try again.', 'error');
    }
  };

  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get plain text from draft content
  const getPlainText = (draftContent) => {
    if (!draftContent) return '';
    if (typeof draftContent === 'string') return draftContent;
    return draftRawToPlainText(draftContent);
  };

  // Helper: create EditorState from a value (plain text or Draft.js raw)
  const createEditorState = (value) => {
    if (!value) return EditorState.createEmpty();
    if (typeof value === 'object' && value.blocks) {
      try { return EditorState.createWithContent(convertFromRaw(value)); } catch (e) { console.warn('Failed to convert Draft.js content:', e); }
    }
    if (typeof value === 'string') return EditorState.createWithContent(ContentState.createFromText(value));
    return EditorState.createEmpty();
  };

  // Lazily initialize editor states when a question card is expanded
  useEffect(() => {
    if (editingQuestionIndex !== null && !editorStates[editingQuestionIndex]) {
      const question = editQuestions[editingQuestionIndex];
      if (question) {
        const choiceEditors = {};
        const numChoices = Math.max(question.choices?.length || 0, 2);
        for (let ci = 0; ci < numChoices; ci++) {
          const choice = question.choices?.find(c => c.choiceId === ci.toString());
          choiceEditors[`choice${CHOICE_LABELS[ci]}`] = createEditorState(
            choice?.text || (question._plainChoices ? question._plainChoices[ci] : null)
          );
        }
        setEditorStates(prev => ({
          ...prev,
          [editingQuestionIndex]: {
            questionText: createEditorState(question.questionText || question._plainQuestionText),
            ...choiceEditors,
            explanation: createEditorState(question.explanationText || question._plainExplanation),
          }
        }));
      }
    }
  }, [editingQuestionIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle WYSIWYG editor changes
  const handleEditorChange = (index, field, editorState) => {
    setEditorStates(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: editorState }
    }));

    const plainText = editorState.getCurrentContent().getPlainText();
    const raw = convertToRaw(editorState.getCurrentContent());

    setEditQuestions(prev => {
      const updated = [...prev];
      const q = { ...updated[index] };

      if (field === 'questionText') {
        q._plainQuestionText = plainText;
        q.questionText = raw;
      } else if (field.startsWith('choice')) {
        const choiceLetter = field.replace('choice', '');
        const choiceIdx = CHOICE_LABELS.indexOf(choiceLetter);
        if (choiceIdx >= 0) {
          if (!q._plainChoices) q._plainChoices = [];
          while (q._plainChoices.length <= choiceIdx) q._plainChoices.push('');
          q._plainChoices[choiceIdx] = plainText;

          const choiceIdStr = choiceIdx.toString();
          if (plainText) {
            const existing = (q.choices || []).find(c => c.choiceId === choiceIdStr);
            if (existing) {
              q.choices = q.choices.map(c => c.choiceId === choiceIdStr ? { ...c, text: raw } : c);
            } else {
              q.choices = [...(q.choices || []), { choiceId: choiceIdStr, text: raw, label: CHOICE_LABELS[choiceIdx] }];
              q.choices.sort((a, b) => a.choiceId.localeCompare(b.choiceId));
            }
          } else if (choiceIdx >= 2) {
            q.choices = (q.choices || []).filter(c => c.choiceId !== choiceIdStr);
          }
        }
      } else if (field === 'explanation') {
        q._plainExplanation = plainText;
        q.explanationText = raw;
      }

      // Re-validate
      const rowErrors = [];
      if (!q.subjectName) rowErrors.push('Subject is required');
      if (!q._plainQuestionText) rowErrors.push('Question Text is required');
      if (!q._plainChoices?.[0]) rowErrors.push('Choice A is required');
      if (!q._plainChoices?.[1]) rowErrors.push('Choice B is required');
      if (!q.correctAnswerLabel) {
        rowErrors.push('Correct Answer is required');
      } else if (!CHOICE_LABELS.includes(q.correctAnswerLabel)) {
        rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
      } else {
        const answerIdx = CHOICE_LABELS.indexOf(q.correctAnswerLabel);
        if (answerIdx >= 2 && !q._plainChoices?.[answerIdx]) {
          rowErrors.push(`Correct Answer is ${q.correctAnswerLabel} but Choice ${q.correctAnswerLabel} is empty`);
        }
      }
      q.hasErrors = rowErrors.length > 0;
      q.errors = rowErrors;

      updated[index] = q;
      return updated;
    });
  };

  // Extract ZIP file into Excel + images
  const extractZip = async (zipFile) => {
    const zip = await JSZip.loadAsync(zipFile);
    let excelFile = null;
    const images = {};
    for (const [path, entry] of Object.entries(zip.files)) {
      if (entry.dir || path.startsWith('__MACOSX')) continue;
      const filename = path.split('/').pop();
      if (!filename) continue;
      const ext = filename.split('.').pop().toLowerCase();
      if (['xlsx', 'xls', 'csv'].includes(ext) && !excelFile) {
        const blob = await entry.async('blob');
        excelFile = new File([blob], filename, { type: blob.type });
      } else if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) {
        const blob = await entry.async('blob');
        images[filename.toLowerCase()] = new File([blob], filename, { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` });
      }
    }
    if (!excelFile) throw new Error('No Excel file found in ZIP.');
    return { excelFile, images };
  };

  // Replace blob: URLs in Draft.js raw content entityMap with Firebase URLs
  const replaceBlobUrl = (raw, blobUrl, firebaseUrl) => {
    if (!raw || !raw.entityMap || !blobUrl || !firebaseUrl) return raw;
    const result = JSON.parse(JSON.stringify(raw));
    Object.values(result.entityMap).forEach(entity => {
      if (entity.type === 'IMAGE' && entity.data?.src === blobUrl) {
        entity.data.src = firebaseUrl;
      }
    });
    return result;
  };

  // Finalize Draft.js raw content for saving: replace blob URL or add Firebase image
  const finalizeRawContent = (content, blobUrl, firebaseUrl, width) => {
    let raw = typeof content === 'object' && content.blocks ? content : textToDraftRaw(content);
    if (blobUrl && firebaseUrl) {
      return replaceBlobUrl(raw, blobUrl, firebaseUrl);
    } else if (!blobUrl && firebaseUrl) {
      return textToDraftRawWithImage(content, firebaseUrl, width);
    }
    return raw;
  };

  // Check if choice at index has content (text or image entity)
  const choiceHasContent = (q, idx) => {
    if (q._plainChoices?.[idx]) return true;
    const choiceRaw = q.choices?.find(c => c.choiceId === idx.toString())?.text;
    return choiceRaw?.entityMap && Object.keys(choiceRaw.entityMap).length > 0;
  };

  // Refs for stable image resize handler (avoids stale closures)
  const editorStatesRef = useRef(editorStates);
  editorStatesRef.current = editorStates;
  const handleEditorChangeRef = useRef(handleEditorChange);
  handleEditorChangeRef.current = handleEditorChange;

  // Block renderer for ResizableImage support in all editors
  const createBlockRenderer = useCallback((questionIndex, field) => (block) => {
    if (block.getType() === 'atomic') {
      return {
        component: ResizableImage,
        editable: false,
        props: {
          onResize: (entityKey, newWidth) => {
            const currentEditorState = editorStatesRef.current[questionIndex]?.[field];
            if (!currentEditorState) return;
            const contentState = currentEditorState.getCurrentContent();
            const newContentState = contentState.mergeEntityData(entityKey, { width: String(newWidth) });
            const newEditorState = EditorState.push(currentEditorState, newContentState, 'apply-entity');
            handleEditorChangeRef.current(questionIndex, field, newEditorState);
          }
        }
      };
    }
    return undefined;
  }, []);

  // Render content as HTML (for Draft.js raw objects) or plain text (for strings)
  const renderContent = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return <span style={{ whiteSpace: 'pre-wrap' }}>{value}</span>;
    if (typeof value === 'object' && value.blocks) return renderDraftContent(value);
    return null;
  };

  // Filter exams by search term
  const filteredExams = useMemo(() => {
    if (!exams) return [];
    if (!searchTerm) return exams;

    const searchLower = searchTerm.toLowerCase();
    return exams.filter(exam =>
      exam.name?.toLowerCase().includes(searchLower) ||
      exam.examId?.toLowerCase().includes(searchLower)
    );
  }, [exams, searchTerm]);

  // Prepare questions for editing (extract plain text from Draft.js)
  const prepareQuestionsForEdit = useCallback((exam) => {
    let examQuestions = exam.questions || [];

    // Legacy: look up questions from questionBankQuestions by questionIds
    if (examQuestions.length === 0 && exam.questionIds?.length > 0) {
      const questionMap = new Map((questions || []).map(q => [q.questionId, q]));
      examQuestions = exam.questionIds
        .map(id => questionMap.get(id))
        .filter(Boolean);
    }

    if (examQuestions.length === 0) return [];

    return examQuestions.map((q, index) => {
      const plainQuestionText = getPlainText(q.questionText);
      const plainExplanation = getPlainText(q.explanationText);

      // Extract plain text for each choice dynamically
      const plainChoices = CHOICE_LABELS.map((_, ci) => {
        const choice = q.choices?.find(c => c.choiceId === ci.toString());
        return choice ? getPlainText(choice.text) : '';
      });

      const correctAnswerIndex = q.correctAnswer?.[0];

      return {
        ...q,
        _index: index,
        _plainQuestionText: plainQuestionText,
        _plainChoices: plainChoices,
        _plainExplanation: plainExplanation,
        correctAnswerLabel: ID_TO_LABEL_MAP[correctAnswerIndex] || '',
        hasErrors: false,
        errors: []
      };
    });
  }, [questions]);

  // Open edit modal — fetch full exam document first (list data is lightweight, no questions)
  const handleEdit = async (exam) => {
    // Show modal immediately with exam metadata while loading questions
    setEditingExam(exam);
    setEditForm({
      name: exam.name || '',
      isActive: exam.isActive !== false
    });
    setEditQuestions([]);
    setEditingQuestionIndex(null);
    setQuestionSearchTerm('');
    setCurrentPage(1);
    setEditorStates({});
    setEditExamLoading(true);
    setEditModalOpen(true);

    // Fetch the full exam document (includes embedded questions array)
    const result = await dispatch(fetchFullLengthExam(exam.examId));
    setEditExamLoading(false);
    if (result.success && result.exam) {
      const fullExam = result.exam;
      setEditingExam(fullExam);
      setEditQuestions(prepareQuestionsForEdit(fullExam));
    }
  };

  // Open edit modal with warning if exam has student attempts
  const handleEditWithWarning = async (exam) => {
    const attemptCount = attemptCounts[exam.examId] || 0;
    if (attemptCount > 0) {
      const result = await Swal.fire({
        title: 'Edit Exam with Attempts?',
        html: `<p>This exam has <strong>${attemptCount} student attempt(s)</strong>.</p><p>Editing questions may affect how past results are displayed. Proceed with caution.</p>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#5e72e4',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, Edit',
        cancelButtonText: 'Cancel'
      });
      if (!result.isConfirmed) return;
    }
    handleEdit(exam);
  };

  // Deep link: open the target exam's edit modal once the exam list has loaded
  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const params = new URLSearchParams(location.search);
    const examId = params.get('examId');
    if (!examId || !exams || exams.length === 0) return;

    const exam = exams.find(e => e.examId === examId);
    deepLinkHandledRef.current = true;
    // Drop the params so a refresh or modal close doesn't re-trigger the deep link
    history.replace(location.pathname);

    if (!exam) {
      Swal.fire('Exam Not Found', `Could not find exam "${examId}". It may have been archived.`, 'warning');
      return;
    }
    setPendingQuestionId(params.get('questionId'));
    handleEdit(exam);
  }, [exams, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link: once questions are loaded, jump to the reported question and expand it
  useEffect(() => {
    if (!pendingQuestionId || editQuestions.length === 0) return;

    const index = editQuestions.findIndex(q => q.questionId === pendingQuestionId);
    setPendingQuestionId(null);

    if (index === -1) {
      Swal.fire('Question Not Found', `Question "${pendingQuestionId}" is no longer part of this exam.`, 'info');
      return;
    }

    setQuestionSearchTerm('');
    setCurrentPage(Math.floor(index / questionsPerPage) + 1);
    setEditingQuestionIndex(index);

    // Wait for the card to render on the new page before scrolling to it
    setTimeout(() => {
      const el = document.getElementById(`exam-question-${index}`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  }, [editQuestions, pendingQuestionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Archive exam (soft delete)
  const handleArchive = async (exam) => {
    const attemptCount = attemptCounts[exam.examId] || 0;
    const result = await Swal.fire({
      title: 'Archive Exam?',
      html: `<p>Are you sure you want to archive "<strong>${exam.name}</strong>"?</p><p class="text-muted mb-0">This exam has <strong>${attemptCount} attempt(s)</strong>. Student results will be preserved, but the exam will no longer appear for students.</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#fb6340',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Archive',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await dispatch(archiveFullLengthExam(exam.examId));
    }
  };

  // Update a question field during review/edit
  const updateQuestion = (index, field, value) => {
    setEditQuestions(prev => {
      const updated = [...prev];
      const q = { ...updated[index] };

      if (field === 'subjectName') {
        q.subjectName = value;
      } else if (field === 'unitName') {
        q.unitName = value;
      } else if (field === 'topicName') {
        q.topicName = value;
      } else if (field === 'questionText') {
        q._plainQuestionText = value;
        q.questionText = textToDraftRaw(value);
      } else if (field.startsWith('choice')) {
        const choiceLetter = field.replace('choice', '');
        const choiceIdx = CHOICE_LABELS.indexOf(choiceLetter);
        if (choiceIdx >= 0) {
          if (!q._plainChoices) q._plainChoices = [];
          while (q._plainChoices.length <= choiceIdx) q._plainChoices.push('');
          q._plainChoices[choiceIdx] = value;

          const choiceIdStr = choiceIdx.toString();
          if (value) {
            const existing = (q.choices || []).find(c => c.choiceId === choiceIdStr);
            if (existing) {
              q.choices = q.choices.map(c => c.choiceId === choiceIdStr ? { ...c, text: textToDraftRaw(value) } : c);
            } else {
              q.choices = [...(q.choices || []), { choiceId: choiceIdStr, text: textToDraftRaw(value), label: CHOICE_LABELS[choiceIdx] }];
              q.choices.sort((a, b) => a.choiceId.localeCompare(b.choiceId));
            }
          } else if (choiceIdx >= 2) {
            q.choices = (q.choices || []).filter(c => c.choiceId !== choiceIdStr);
          }
        }
      } else if (field === 'correctAnswer') {
        q.correctAnswerLabel = value;
        q.correctAnswer = value ? [CORRECT_ANSWER_MAP[value]] : [];
      } else if (field === 'explanation') {
        q._plainExplanation = value;
        q.explanationText = textToDraftRaw(value);
      }

      // Re-validate
      const rowErrors = [];
      if (!q.subjectName) rowErrors.push('Subject is required');
      if (!q._plainQuestionText) rowErrors.push('Question Text is required');
      if (!q._plainChoices?.[0]) rowErrors.push('Choice A is required');
      if (!q._plainChoices?.[1]) rowErrors.push('Choice B is required');
      if (!q.correctAnswerLabel) {
        rowErrors.push('Correct Answer is required');
      } else if (!CHOICE_LABELS.includes(q.correctAnswerLabel)) {
        rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
      } else {
        const answerIdx = CHOICE_LABELS.indexOf(q.correctAnswerLabel);
        if (answerIdx >= 2 && !q._plainChoices?.[answerIdx]) {
          rowErrors.push(`Correct Answer is ${q.correctAnswerLabel} but Choice ${q.correctAnswerLabel} is empty`);
        }
      }
      q.hasErrors = rowErrors.length > 0;
      q.errors = rowErrors;

      updated[index] = q;
      return updated;
    });
  };

  // Delete a question from the edit list
  const deleteEditQuestion = (index) => {
    setEditQuestions(prev => prev.filter((_, i) => i !== index));
    if (editingQuestionIndex === index) setEditingQuestionIndex(null);
  };

  // Handle correct answer toggle via radio button
  const handleCorrectAnswerToggle = (questionIndex, choiceIdx) => {
    updateQuestion(questionIndex, 'correctAnswer', CHOICE_LABELS[choiceIdx]);
  };

  // Get choice label from index
  const getChoiceLabel = (index) => String.fromCharCode(65 + index);

  // Parse an Excel file (with optional ZIP images) to replace questions
  const parseReUploadFile = (file, zipImages = {}) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        let sheetName = workbook.SheetNames.find(name =>
          name.toLowerCase().includes('questions') || name.toLowerCase().includes('template')
        );
        if (!sheetName) {
          sheetName = workbook.SheetNames.find(name =>
            !['instructions', 'reference_data', 'lists'].includes(name.toLowerCase())
          );
        }
        if (!sheetName) sheetName = workbook.SheetNames[0];

        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'File appears to be empty.', confirmButtonColor: '#5e72e4' });
          return;
        }

        const newQuestions = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0 || row.every(cell => !cell || cell.toString().trim() === '')) continue;

          const subject = row[0]?.toString().trim() || '';
          const unit = row[1]?.toString().trim() || '';
          const topic = row[2]?.toString().trim() || '';
          const questionText = row[3]?.toString().trim() || '';
          const choiceTexts = CHOICE_LABELS.map((_, ci) => row[FIRST_CHOICE_COL + ci]?.toString().trim() || '');
          const correctAnswer = row[FIRST_CHOICE_COL + MAX_CHOICES]?.toString().trim().toUpperCase() || '';
          const explanation = row[FIRST_CHOICE_COL + MAX_CHOICES + 1]?.toString().trim() || '';

          // Read image filenames and widths (optional columns, alternating: image, width, image, width, ...)
          const questionImageFilename = row[QUESTION_IMAGE_COL]?.toString().trim() || '';
          const questionImageWidth = row[QUESTION_IMAGE_WIDTH_COL]?.toString().trim() || '250';
          const choiceImageFilenames = CHOICE_LABELS.map((_, ci) => row[FIRST_CHOICE_IMAGE_COL + ci * 2]?.toString().trim() || '');
          const choiceImageWidths = CHOICE_LABELS.map((_, ci) => row[FIRST_CHOICE_IMAGE_COL + ci * 2 + 1]?.toString().trim() || '150');
          const explanationImageFilename = row[EXPLANATION_IMAGE_COL]?.toString().trim() || '';
          const explanationImageWidth = row[EXPLANATION_IMAGE_WIDTH_COL]?.toString().trim() || '250';

          const rowErrors = [];
          if (!subject) rowErrors.push('Subject is required');
          if (!questionText) rowErrors.push('Question Text is required');
          if (!choiceTexts[0] && !choiceImageFilenames[0]) rowErrors.push('Choice A is required (text or image)');
          if (!choiceTexts[1] && !choiceImageFilenames[1]) rowErrors.push('Choice B is required (text or image)');
          if (!correctAnswer) {
            rowErrors.push('Correct Answer is required');
          } else if (!CHOICE_LABELS.includes(correctAnswer)) {
            rowErrors.push(`Correct Answer must be one of: ${ANSWER_DROPDOWN_STRING}`);
          } else {
            const correctIdx = CHOICE_LABELS.indexOf(correctAnswer);
            if (!choiceTexts[correctIdx] && !choiceImageFilenames[correctIdx]) {
              rowErrors.push(`Correct Answer is ${correctAnswer} but Choice ${correctAnswer} is empty`);
            }
          }

          // Create blob URLs for image preview
          let questionImageBlobUrl = null;
          if (questionImageFilename && zipImages[questionImageFilename.toLowerCase()]) {
            questionImageBlobUrl = URL.createObjectURL(zipImages[questionImageFilename.toLowerCase()]);
          }
          const choiceImageBlobUrls = choiceImageFilenames.map(fn => {
            if (fn && zipImages[fn.toLowerCase()]) return URL.createObjectURL(zipImages[fn.toLowerCase()]);
            return null;
          });
          let explanationImageBlobUrl = null;
          if (explanationImageFilename && zipImages[explanationImageFilename.toLowerCase()]) {
            explanationImageBlobUrl = URL.createObjectURL(zipImages[explanationImageFilename.toLowerCase()]);
          }

          // Build choices (include choices with text and/or image)
          const choices = [];
          choiceTexts.forEach((text, ci) => {
            if (text || choiceImageFilenames[ci]) {
              const choiceBlobUrl = choiceImageBlobUrls[ci];
              choices.push({ choiceId: ci.toString(), text: choiceBlobUrl ? textToDraftRawWithImage(text, choiceBlobUrl, choiceImageWidths[ci]) : textToDraftRaw(text || ' '), label: CHOICE_LABELS[ci] });
            }
          });

          // Resolve subject/unit/topic to database IDs
          const matchedSubject = resolveSubject(subject, subjects);
          const matchedUnit = resolveUnit(unit, units, matchedSubject?.subjectId);
          const matchedTopic = topic ? resolveTopic(topic, topics, matchedUnit?.unitId) : null;

          newQuestions.push({
            rowNumber: i + 1,
            subjectName: subject,
            unitName: unit,
            topicName: topic,
            subjectId: matchedSubject?.subjectId || null,
            unitId: matchedUnit?.unitId || null,
            topicId: matchedTopic?.topicId || null,
            questionText: questionImageBlobUrl ? textToDraftRawWithImage(questionText, questionImageBlobUrl, questionImageWidth) : textToDraftRaw(questionText),
            choices,
            correctAnswer: correctAnswer ? [CORRECT_ANSWER_MAP[correctAnswer]] : [],
            correctAnswerLabel: correctAnswer,
            explanationText: explanationImageBlobUrl ? textToDraftRawWithImage(explanation, explanationImageBlobUrl, explanationImageWidth) : textToDraftRaw(explanation),
            _plainQuestionText: questionText,
            _plainChoices: choiceTexts,
            _plainExplanation: explanation,
            questionImageFilename,
            choiceImageFilenames,
            explanationImageFilename,
            questionImageWidth,
            choiceImageWidths,
            explanationImageWidth,
            questionImageBlobUrl,
            choiceImageBlobUrls,
            explanationImageBlobUrl,
            hasErrors: rowErrors.length > 0,
            errors: rowErrors
          });
        }

        if (newQuestions.length === 0) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'No valid question rows found.', confirmButtonColor: '#5e72e4' });
          return;
        }

        setEditQuestions(newQuestions);
        setEditingQuestionIndex(null);
        setCurrentPage(1);
        setQuestionSearchTerm('');
        setEditorStates({});

        Swal.fire({
          icon: 'success',
          title: 'File Parsed',
          text: `${newQuestions.length} questions loaded. Review and save.`,
          confirmButtonColor: '#5e72e4',
          timer: 2000
        });
      } catch (err) {
        console.error('Parse error:', err);
        Swal.fire({ icon: 'error', title: 'Error', text: `Failed to parse file: ${err.message}`, confirmButtonColor: '#5e72e4' });
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Re-upload Excel/ZIP to replace questions
  const handleReUpload = useCallback(async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;
    setReUploadFile(selectedFile); // capture for logging
    e.target.value = '';

    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (ext === 'zip') {
      try {
        const { excelFile, images } = await extractZip(selectedFile);
        setImageFiles(images);
        parseReUploadFile(excelFile, images);
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#5e72e4' });
      }
    } else {
      setImageFiles({});
      parseReUploadFile(selectedFile);
    }
  }, [subjects, units, topics]);

  // Filtered questions for search
  const displayedQuestions = questionSearchTerm
    ? editQuestions.filter((q, i) => {
        const text = (q._plainQuestionText || '').toLowerCase();
        const subject = (q.subjectName || '').toLowerCase();
        const term = questionSearchTerm.toLowerCase();
        return text.includes(term) || subject.includes(term) || String(i + 1).includes(term);
      })
    : editQuestions;

  // Pagination
  const totalPages = Math.ceil(displayedQuestions.length / questionsPerPage);
  const paginatedQuestions = displayedQuestions.slice(
    (currentPage - 1) * questionsPerPage,
    currentPage * questionsPerPage
  );

  const editErrorCount = editQuestions.filter(q => q.hasErrors).length;
  const editValidCount = editQuestions.length - editErrorCount;
  const editSubjectCounts = editQuestions.reduce((acc, q) => {
    if (q.subjectName) acc[q.subjectName] = (acc[q.subjectName] || 0) + 1;
    return acc;
  }, {});

  // Save edit
  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Please enter an exam name.', confirmButtonColor: '#5e72e4' });
      return;
    }

    if (editQuestions.length > 0 && editErrorCount > 0) {
      Swal.fire({ icon: 'error', title: 'Error', text: `Please fix ${editErrorCount} question(s) with errors before saving.`, confirmButtonColor: '#5e72e4' });
      return;
    }

    const updates = {
      name: editForm.name.trim(),
      isActive: editForm.isActive
    };

    // If there are questions to save, update them (also converts legacy exams to embedded format)
    if (editQuestions.length > 0) {
      // Upload images from ZIP if any
      const imageUrlMap = {};
      const allFilenames = new Set();
      editQuestions.forEach(q => {
        if (q.questionImageFilename) allFilenames.add(q.questionImageFilename.toLowerCase());
        (q.choiceImageFilenames || []).forEach(fn => { if (fn) allFilenames.add(fn.toLowerCase()); });
        if (q.explanationImageFilename) allFilenames.add(q.explanationImageFilename.toLowerCase());
      });

      if (allFilenames.size > 0 && Object.keys(imageFiles).length > 0) {
        setIsUploadingImages(true);
        setImageUploadProgress(0);
        const fns = Array.from(allFilenames);
        for (let i = 0; i < fns.length; i++) {
          const fn = fns[i];
          const imgFile = imageFiles[fn];
          if (imgFile) {
            try {
              const optimized = await optimizeImage(imgFile, { maxW: 800, quality: 0.8 });
              const url = await uploadImage(optimized, getSpecialtyStorageFolder('question-bank-images', activeSpecialty));
              imageUrlMap[fn] = url;
            } catch (err) {
              console.error(`Failed to upload image ${fn}:`, err);
            }
          }
          setImageUploadProgress(Math.round(((i + 1) / fns.length) * 100));
        }
        setIsUploadingImages(false);
      }

      // Re-assign question IDs and strip internal fields
      const cleanQuestions = editQuestions.map((q, index) => {
        const qImgUrl = imageUrlMap[q.questionImageFilename?.toLowerCase()] || null;
        const expImgUrl = imageUrlMap[q.explanationImageFilename?.toLowerCase()] || null;
        return {
          questionId: q.questionId || `${editingExam.examId}-Q${String(index + 1).padStart(4, '0')}`,
          subjectName: q.subjectName || '',
          unitName: q.unitName || '',
          topicName: q.topicName || '',
          subjectId: q.subjectId || null,
          unitId: q.unitId || null,
          topicId: q.topicId || null,
          questionText: finalizeRawContent(q.questionText, q.questionImageBlobUrl, qImgUrl, q.questionImageWidth),
          choices: (q.choices || []).map(c => {
            const choiceIdx = parseInt(c.choiceId);
            const cImgUrl = imageUrlMap[(q.choiceImageFilenames || [])[choiceIdx]?.toLowerCase()] || null;
            const cBlobUrl = (q.choiceImageBlobUrls || [])[choiceIdx] || null;
            return { choiceId: c.choiceId, text: finalizeRawContent(c.text, cBlobUrl, cImgUrl, (q.choiceImageWidths || [])[choiceIdx]) };
          }),
          correctAnswer: q.correctAnswer,
          explanationText: finalizeRawContent(q.explanationText, q.explanationImageBlobUrl, expImgUrl, q.explanationImageWidth),
        };
      });

      updates.questions = cleanQuestions;
      updates.questionCount = cleanQuestions.length;
      updates.timeLimit = cleanQuestions.length * 66;
      updates.hasEmbeddedQuestions = true;
      // Clear legacy questionIds since questions are now embedded
      if (editingExam.questionIds) {
        updates.questionIds = null;
      }
    }

    const result = await dispatch(updateFullLengthExam(editingExam.examId, updates));

    if (result.success) {
      if (reUploadFile) {
        const batchId = generateBatchId();
        const { storagePath, downloadUrl } = await uploadBatchFile(reUploadFile, 'full_length', batchId, activeSpecialty);
        await dispatch(saveUploadLog('full_length', {
          batchId,
          type: 'edit',
          fileName: reUploadFile.name,
          storagePath,
          downloadUrl,
          uploadedBy: userHandle || 'unknown',
          examId: editingExam.examId,
          examName: editingExam.name,
          questionCount: editQuestions.length,
          successCount: editQuestions.length,
          failCount: 0,
          errors: [],
        }));
        setReUploadFile(null);
      }
      setEditModalOpen(false);
      setEditingExam(null);
      setEditQuestions([]);
    }
  };

  // Delete exam
  const handleDelete = async (exam) => {
    const result = await Swal.fire({
      title: 'Delete Exam?',
      text: `Are you sure you want to delete "${exam.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      await dispatch(deleteFullLengthExam(exam.examId));
    }
  };

  // Toggle active status
  const handleToggleActive = async (exam) => {
    await dispatch(updateFullLengthExam(exam.examId, {
      isActive: !exam.isActive
    }));
  };

  // Close edit/view modal
  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingExam(null);
    setEditQuestions([]);
    setEditingQuestionIndex(null);
    setQuestionSearchTerm('');
    setCurrentPage(1);
    setEditExamLoading(false);
    setEditorStates({});
  };

  return (
    <>
      {/* Header */}
      <div className="header bg-gradient-success pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center">
              <Col>
                <h1 className="text-white mb-0">Full-Length Exams</h1>
                <p className="text-white mt-0 mb-0">Manage full-length practice exams for students</p>
              </Col>
              <Col className="text-right">
                <Button
                  color="primary"
                  onClick={() => history.push('/admin/question-bank/full-length-exams/create')}
                >
                  <i className="fas fa-plus mr-2"></i>
                  Create New Exam
                </Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>

      {/* Page content */}
      <Container className="mt--7" fluid>
        <Card className="shadow">
          <CardHeader>
            <Row className="align-items-center">
              <Col md="4">
                <h3 className="mb-0">All Exams ({filteredExams.length})</h3>
              </Col>
              <Col md="5">
                <InputGroup>
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="fas fa-search"></i>
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    type="text"
                    placeholder="Search exams..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md="3" className="text-right">
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleRebuildCache}
                  disabled={isRebuildingCache}
                >
                  {isRebuildingCache ? (
                    <><Spinner size="sm" className="mr-1" /> Refreshing...</>
                  ) : (
                    <><i className="fas fa-sync-alt mr-1"></i> Refresh Cache</>
                  )}
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            {examsLoading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2 text-muted">Loading exams...</p>
              </div>
            ) : filteredExams.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                <h4 className="text-muted">No Exams Found</h4>
                <p className="text-muted">
                  {searchTerm
                    ? 'Try adjusting your search term.'
                    : 'Create your first full-length exam to get started.'}
                </p>
                {!searchTerm && (
                  <Button
                    color="primary"
                    onClick={() => history.push('/admin/question-bank/full-length-exams/create')}
                  >
                    <i className="fas fa-plus mr-2"></i>
                    Create Exam
                  </Button>
                )}
              </div>
            ) : (
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Batch ID</th>
                    <th>Questions</th>
                    <th>Time Limit</th>
                    <th>Selection</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredExams.map(exam => (
                    <tr key={exam.examId}>
                      <td>
                        <div>
                          <strong>{exam.name}</strong>
                          <br />
                          <small className="text-muted">{exam.examId}</small>
                        </div>
                      </td>
                      <td>
                        {exam.batchId
                          ? <small className="font-weight-bold">{exam.batchId}</small>
                          : <small className="text-muted">—</small>
                        }
                      </td>
                      <td>
                        {exam.questionCount || exam.questions?.length || exam.questionIds?.length || 0}
                        {getArchivedCount(exam) > 0 && (
                          <>
                            {' '}
                            <Badge color="warning" pill id={`archived-${exam.examId}`}>
                              <i className="fas fa-archive mr-1"></i>{getArchivedCount(exam)} archived
                            </Badge>
                            <UncontrolledTooltip target={`archived-${exam.examId}`}>
                              {getArchivedCount(exam)} question(s) in this exam have been archived. Students will only see the remaining active questions. Consider editing this exam to update the question list.
                            </UncontrolledTooltip>
                          </>
                        )}
                      </td>
                      <td>
                        {exam.hasSections ? (
                          <span>
                            {formatTime(exam.timeLimit)}
                            <br />
                            <small className="text-muted">{exam.sections?.length || 0} sections</small>
                          </span>
                        ) : formatTime(exam.timeLimit)}
                      </td>
                      <td>
                        {exam.questions ? (
                          <Badge color="success">Excel Upload</Badge>
                        ) : (
                          <Badge color={exam.selectionMode === 'manual' ? 'info' : 'primary'}>
                            {exam.selectionMode === 'manual' ? 'Manual' : 'Auto'}
                          </Badge>
                        )}
                      </td>
                      <td>
                        <Badge
                          color={exam.isActive ? 'success' : 'secondary'}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleToggleActive(exam)}
                          id={`status-${exam.examId}`}
                        >
                          {exam.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <UncontrolledTooltip target={`status-${exam.examId}`}>
                          Click to toggle status
                        </UncontrolledTooltip>
                      </td>
                      <td>{formatDate(exam.createdAt)}</td>
                      <td className="text-right">
                        {(attemptCounts[exam.examId] || 0) > 0 ? (
                          <>
                            <Badge color="light" className="mr-2 text-dark" id={`attempts-${exam.examId}`}>
                              <i className="fas fa-users mr-1"></i>{attemptCounts[exam.examId]}
                            </Badge>
                            <UncontrolledTooltip target={`attempts-${exam.examId}`}>
                              {attemptCounts[exam.examId]} attempt(s)
                            </UncontrolledTooltip>
                            <Button
                              color="info"
                              size="sm"
                              className="mr-2"
                              onClick={() => handleEditWithWarning(exam)}
                              id={`edit-attempts-${exam.examId}`}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <UncontrolledTooltip target={`edit-attempts-${exam.examId}`}>
                              Edit Exam
                            </UncontrolledTooltip>
                            <Button
                              color="warning"
                              size="sm"
                              onClick={() => handleArchive(exam)}
                              disabled={deleteLoading}
                              id={`archive-${exam.examId}`}
                            >
                              <i className="fas fa-archive"></i>
                            </Button>
                            <UncontrolledTooltip target={`archive-${exam.examId}`}>
                              Archive Exam
                            </UncontrolledTooltip>
                          </>
                        ) : (
                          <>
                            <Button
                              color="info"
                              size="sm"
                              className="mr-2"
                              onClick={() => handleEdit(exam)}
                              id={`edit-${exam.examId}`}
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <UncontrolledTooltip target={`edit-${exam.examId}`}>
                              Edit Exam
                            </UncontrolledTooltip>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(exam)}
                              disabled={deleteLoading}
                              id={`delete-${exam.examId}`}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                            <UncontrolledTooltip target={`delete-${exam.examId}`}>
                              Delete Exam
                            </UncontrolledTooltip>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </CardBody>
        </Card>

      </Container>

      {/* Edit Modal - Full Review/Edit */}
      <Modal isOpen={editModalOpen} toggle={closeEditModal} size="xl" style={{ maxWidth: '95%' }}>
        <ModalHeader toggle={closeEditModal}>
          <i className="fas fa-edit mr-2 text-primary"></i>
          Edit Exam
          {editingExam && (
            <Badge color="info" className="ml-2">{editingExam.examId}</Badge>
          )}
          {editingExam && (attemptCounts[editingExam.examId] || 0) > 0 && (
            <Badge color="warning" className="ml-2">
              <i className="fas fa-exclamation-triangle mr-1"></i>
              {attemptCounts[editingExam.examId]} attempt(s)
            </Badge>
          )}
        </ModalHeader>
        <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {editingExam && (
            <Row>
              {/* Main Content */}
              <Col lg="8">
                {/* Exam Details */}
                <Card className="shadow-sm mb-4">
                  <CardHeader>
                    <h3 className="mb-0">Exam Details</h3>
                  </CardHeader>
                  <CardBody>
                    <Row>
                      <Col md="8">
                        <FormGroup>
                          <Label className="form-control-label">Exam Name *</Label>
                          <Input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                            placeholder="Enter exam name"
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label className="form-control-label">Status</Label>
                          <div className="custom-control custom-checkbox mt-2">
                            <input
                              type="checkbox"
                              id="editIsActive"
                              className="custom-control-input"
                              checked={editForm.isActive}
                              onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                            />
                            <label className="custom-control-label" htmlFor="editIsActive">
                              Available to students
                            </label>
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                {/* Re-upload option */}
                {(editQuestions.length > 0 || editingExam.questionIds?.length > 0) && (
                  <Card className="shadow-sm mb-4">
                    <CardHeader className="d-flex justify-content-between align-items-center">
                      <div>
                        <h3 className="mb-0 d-inline">Replace Questions (Optional)</h3>
                        <small className="text-muted d-block">Upload a new Excel file to replace all questions</small>
                      </div>
                      <Button color="info" size="sm" onClick={downloadTemplate}>
                        <i className="fas fa-download mr-1"></i>
                        Template
                      </Button>
                    </CardHeader>
                    <CardBody>
                      <div
                        onClick={() => document.getElementById('reupload-file').click()}
                        style={{
                          border: '2px dashed #dee2e6',
                          borderRadius: '8px',
                          padding: '20px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: '#f8f9fe'
                        }}
                      >
                        <input
                          type="file"
                          id="reupload-file"
                          accept=".xlsx,.xls,.csv,.zip"
                          onChange={handleReUpload}
                          style={{ display: 'none' }}
                        />
                        <i className="fas fa-cloud-upload-alt fa-2x text-muted mb-2" style={{ display: 'block' }}></i>
                        <p className="mb-0 small">Click to upload a new Excel or ZIP file to replace questions</p>
                        <small className="text-muted">Supports .xlsx, .xls, .csv, .zip (with images)</small>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Image upload progress */}
                {isUploadingImages && (
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <Spinner size="sm" color="warning" className="mr-2" />
                      <span>Uploading images... {imageUploadProgress}%</span>
                    </div>
                    <Progress value={imageUploadProgress} color="warning" />
                  </div>
                )}

                {/* Review Questions - Card-based WYSIWYG */}
                {editQuestions.length > 0 && (
                  <>
                    <style>{editorContainerStyles}</style>
                    {/* Summary Stats */}
                    <Row className="mb-3">
                      <Col md="3">
                        <Alert color="info" className="mb-0 py-2">
                          <i className="fas fa-list mr-2"></i>
                          Total: <strong>{editQuestions.length}</strong>
                        </Alert>
                      </Col>
                      <Col md="3">
                        <Alert color="success" className="mb-0 py-2">
                          <i className="fas fa-check mr-2"></i>
                          Valid: <strong>{editValidCount}</strong>
                        </Alert>
                      </Col>
                      <Col md="3">
                        <Alert color={editErrorCount > 0 ? 'warning' : 'success'} className="mb-0 py-2">
                          <i className="fas fa-exclamation-triangle mr-2"></i>
                          Errors: <strong>{editErrorCount}</strong>
                        </Alert>
                      </Col>
                      <Col md="3">
                        <InputGroup size="sm">
                          <InputGroupAddon addonType="prepend">
                            <InputGroupText><i className="fas fa-search"></i></InputGroupText>
                          </InputGroupAddon>
                          <Input
                            type="text"
                            placeholder="Search questions..."
                            value={questionSearchTerm}
                            onChange={(e) => { setQuestionSearchTerm(e.target.value); setCurrentPage(1); }}
                          />
                        </InputGroup>
                      </Col>
                    </Row>

                    {/* Hint */}
                    <Card className="mb-3 bg-lighter">
                      <CardBody className="py-2">
                        <div className="text-muted">
                          <i className="fas fa-info-circle mr-1"></i>
                          Click <i className="fas fa-edit"></i> to edit a question inline, or <i className="fas fa-trash-alt text-danger"></i> to remove it.
                        </div>
                      </CardBody>
                    </Card>

                    {/* Question Cards */}
                    {paginatedQuestions.map((question) => {
                      const realIndex = editQuestions.indexOf(question);
                      const isEditing = editingQuestionIndex === realIndex;
                      const hasErrors = question.hasErrors;
                      const isValid = !hasErrors && question.subjectName && question._plainQuestionText;

                      return (
                        <Card
                          key={realIndex}
                          id={`exam-question-${realIndex}`}
                          className={`mb-3 ${hasErrors ? 'border-danger' : isValid ? 'border-success' : 'border-warning'}`}
                          style={{ borderWidth: '2px' }}
                        >
                          <CardHeader className={`py-2 ${hasErrors ? 'bg-danger text-white' : isValid ? 'bg-success text-white' : 'bg-warning'}`}>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center flex-wrap">
                                <Badge color="light" className="mr-2 text-dark">
                                  Q{realIndex + 1}
                                </Badge>
                                {question.questionId && (
                                  <Badge color="light" className="mr-2 text-dark">
                                    <small>{question.questionId}</small>
                                  </Badge>
                                )}
                                {question.subjectName && (
                                  <Badge color="primary" className="mr-2">{question.subjectName}</Badge>
                                )}
                                {question.unitName && (
                                  <Badge color="info" className="mr-2">{question.unitName}</Badge>
                                )}
                                {question.topicName && (
                                  <Badge color="light" className="mr-2 text-dark">{question.topicName}</Badge>
                                )}
                              </div>
                              <div className="d-flex align-items-center">
                                {hasErrors && (
                                  <Badge color="light" className="mr-2 text-danger">
                                    <i className="fas fa-times mr-1"></i>
                                    {question.errors.length} Error{question.errors.length > 1 ? 's' : ''}
                                  </Badge>
                                )}
                                <Button
                                  color={isEditing ? 'light' : 'warning'}
                                  size="sm"
                                  onClick={() => setEditingQuestionIndex(isEditing ? null : realIndex)}
                                  className="ml-2"
                                >
                                  <i className={`fas fa-${isEditing ? 'times' : 'edit'} mr-1`}></i>
                                  {isEditing ? 'Close' : 'Edit'}
                                </Button>
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() => deleteEditQuestion(realIndex)}
                                  className="ml-1"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardBody>
                            {/* Validation Errors */}
                            {hasErrors && !isEditing && (
                              <Alert color="danger" className="mb-3 py-2">
                                <strong>Errors:</strong> {question.errors.join(', ')}
                              </Alert>
                            )}

                            {/* Edit Mode */}
                            <Collapse isOpen={isEditing}>
                              <Card className="mb-3 bg-lighter">
                                <CardBody>
                                  {/* Subject, Unit, Topic */}
                                  <Row>
                                    <Col md="4">
                                      <FormGroup>
                                        <Label className="form-control-label">
                                          Subject <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                          value={question.subjectName || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'subjectName', e.target.value)}
                                          placeholder="Enter subject name"
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col md="4">
                                      <FormGroup>
                                        <Label className="form-control-label">Unit</Label>
                                        <Input
                                          value={question.unitName || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'unitName', e.target.value)}
                                          placeholder="Enter unit name"
                                        />
                                      </FormGroup>
                                    </Col>
                                    <Col md="4">
                                      <FormGroup>
                                        <Label className="form-control-label">Topic (Optional)</Label>
                                        <Input
                                          value={question.topicName || ''}
                                          onChange={(e) => updateQuestion(realIndex, 'topicName', e.target.value)}
                                          placeholder="Enter topic name"
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>

                                  {/* Question Text - WYSIWYG */}
                                  <FormGroup>
                                    <Label className="form-control-label">
                                      Question Text <span className="text-danger">*</span>
                                    </Label>
                                    {editorStates[realIndex] && (
                                      <div className="border rounded p-2 question-editor-container">
                                        <Editor
                                          editorState={editorStates[realIndex].questionText}
                                          onEditorStateChange={(state) => handleEditorChange(realIndex, 'questionText', state)}
                                          toolbar={questionTextToolbar}
                                          customBlockRenderFunc={createBlockRenderer(realIndex, 'questionText')}
                                        />
                                      </div>
                                    )}
                                  </FormGroup>

                                  {/* Answer Choices with Radio Buttons - WYSIWYG */}
                                  {editorStates[realIndex] && (
                                    <>
                                      <FormGroup>
                                        <Label className="form-control-label">
                                          Answer Choices <span className="text-danger">*</span> (Select the correct answer)
                                        </Label>
                                        {Array.from({ length: Math.max(question.choices?.length || 0, 2) }, (_, choiceIdx) => {
                                          const choiceField = `choice${CHOICE_LABELS[choiceIdx]}`;
                                          return (
                                            <div key={choiceIdx} className="mb-3 d-flex align-items-start">
                                              <div className="d-flex align-items-center mr-2" style={{ minWidth: '60px', paddingTop: '10px' }}>
                                                <div className="custom-control custom-radio">
                                                  <input
                                                    type="radio"
                                                    className="custom-control-input"
                                                    id={`edit-choice-${realIndex}-${choiceIdx}`}
                                                    name={`edit-correctAnswer-${realIndex}`}
                                                    checked={question.correctAnswerLabel === CHOICE_LABELS[choiceIdx]}
                                                    onChange={() => handleCorrectAnswerToggle(realIndex, choiceIdx)}
                                                  />
                                                  <label className="custom-control-label" htmlFor={`edit-choice-${realIndex}-${choiceIdx}`}></label>
                                                </div>
                                                <span className="ml-2 font-weight-bold text-muted">
                                                  {CHOICE_LABELS[choiceIdx]}.
                                                  {choiceIdx < 2 && <span className="text-danger">*</span>}
                                                </span>
                                              </div>
                                              <div className="flex-grow-1">
                                                {editorStates[realIndex][choiceField] && (
                                                  <div className="border rounded p-2 choice-editor-container">
                                                    <Editor
                                                      editorState={editorStates[realIndex][choiceField]}
                                                      onEditorStateChange={(state) => handleEditorChange(realIndex, choiceField, state)}
                                                      toolbar={choiceToolbar}
                                                      customBlockRenderFunc={createBlockRenderer(realIndex, choiceField)}
                                                    />
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </FormGroup>

                                      {/* Explanation - WYSIWYG */}
                                      <FormGroup>
                                        <Label className="form-control-label">Explanation</Label>
                                        <div className="border rounded p-2 explanation-editor-container">
                                          <Editor
                                            editorState={editorStates[realIndex].explanation}
                                            onEditorStateChange={(state) => handleEditorChange(realIndex, 'explanation', state)}
                                            toolbar={explanationToolbar}
                                            customBlockRenderFunc={createBlockRenderer(realIndex, 'explanation')}
                                          />
                                        </div>
                                      </FormGroup>
                                    </>
                                  )}

                                  {hasErrors && (
                                    <Alert color="danger" className="mb-0">
                                      <strong>Remaining Errors:</strong> {question.errors.join(', ')}
                                    </Alert>
                                  )}
                                </CardBody>
                              </Card>
                            </Collapse>

                            {/* Preview Mode (when not editing) */}
                            {!isEditing && (
                              <>
                                <div className="mb-3">
                                  <h5 className="mb-2 text-muted">
                                    <i className="fas fa-question-circle mr-2"></i>
                                    Question:
                                  </h5>
                                  <div className="mb-0">
                                    {renderContent(question.questionText) || <span className="text-danger font-italic">Missing question text</span>}
                                  </div>
                                </div>

                                <div className="mb-3">
                                  <h5 className="mb-2 text-muted">
                                    <i className="fas fa-list-ul mr-2"></i>
                                    Choices:
                                  </h5>
                                  {(question.choices || []).map((choice, idx) => (
                                    <div
                                      key={idx}
                                      className={`d-flex align-items-start mb-2 p-2 rounded ${
                                        question.correctAnswerLabel === CHOICE_LABELS[parseInt(choice.choiceId)]
                                          ? 'bg-success text-white'
                                          : 'bg-lighter'
                                      }`}
                                    >
                                      <Badge
                                        color="light"
                                        className={`mr-2 ${question.correctAnswerLabel !== CHOICE_LABELS[parseInt(choice.choiceId)] ? 'text-dark' : ''}`}
                                        style={{ minWidth: '28px' }}
                                      >
                                        {question.correctAnswerLabel === CHOICE_LABELS[parseInt(choice.choiceId)] ? '\u2713' : CHOICE_LABELS[parseInt(choice.choiceId)]}
                                      </Badge>
                                      <div style={{ flex: 1 }}>
                                        {renderContent(choice.text) || <span className="text-danger font-italic">Empty</span>}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {question.explanationText && (
                                  <div className="mb-3">
                                    <h5 className="mb-2 text-muted">
                                      <i className="fas fa-lightbulb mr-2"></i>
                                      Explanation:
                                    </h5>
                                    <div className="mb-0 text-muted">
                                      {renderContent(question.explanationText)}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center py-3">
                        <small className="text-muted">
                          Showing {((currentPage - 1) * questionsPerPage) + 1}-{Math.min(currentPage * questionsPerPage, displayedQuestions.length)} of {displayedQuestions.length}
                        </small>
                        <div>
                          <Button size="sm" color="secondary" outline className="mr-1" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                            <i className="fas fa-angle-double-left"></i>
                          </Button>
                          <Button size="sm" color="secondary" outline className="mr-1" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                            <i className="fas fa-angle-left"></i>
                          </Button>
                          <span className="mx-2 small">Page {currentPage} of {totalPages}</span>
                          <Button size="sm" color="secondary" outline className="mr-1" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                            <i className="fas fa-angle-right"></i>
                          </Button>
                          <Button size="sm" color="secondary" outline disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                            <i className="fas fa-angle-double-right"></i>
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Loading / no questions messages */}
                {editQuestions.length === 0 && editExamLoading && (
                  <Alert color="info">
                    <Spinner size="sm" className="mr-2" />
                    Loading exam questions...
                  </Alert>
                )}
                {editQuestions.length === 0 && !editExamLoading && editingExam.questionIds?.length > 0 && (
                  <Alert color="warning">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    This exam references {editingExam.questionIds.length} Question Bank questions but they could not be resolved. Some questions may have been deleted. You can re-upload questions using the Replace Questions section above.
                  </Alert>
                )}
                {editQuestions.length === 0 && !editExamLoading && !editingExam.questionIds?.length && !editingExam.questions?.length && (
                  <Alert color="info">
                    <i className="fas fa-info-circle mr-2"></i>
                    No questions found for this exam. You can upload questions using the Replace Questions section above.
                  </Alert>
                )}
              </Col>

              {/* Summary Sidebar */}
              <Col lg="4">
                <Card className="shadow-sm" style={{ position: 'sticky', top: '10px' }}>
                  <CardHeader>
                    <h3 className="mb-0">Exam Summary</h3>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-3">
                      <strong>ID:</strong>
                      <p className="mb-0"><small className="text-muted">{editingExam.examId}</small></p>
                    </div>
                    <div className="mb-3">
                      <strong>Name:</strong>
                      <p className="mb-0">{editForm.name || <span className="text-muted">Not set</span>}</p>
                    </div>
                    <div className="mb-3">
                      <strong>Questions:</strong>
                      <p className="mb-0">
                        {editQuestions.length > 0 ? (
                          <>
                            {editQuestions.length} total
                            {editErrorCount > 0 && <span className="text-danger"> ({editErrorCount} with errors)</span>}
                          </>
                        ) : (
                          <span className="text-muted">
                            {editingExam.questionCount || editingExam.questionIds?.length || 0} (legacy)
                          </span>
                        )}
                      </p>
                    </div>
                    {editQuestions.length > 0 && (
                      <div className="mb-3">
                        <strong>Time Limit:</strong>
                        <p className="mb-0">{formatTime(editQuestions.length * 66)}</p>
                      </div>
                    )}
                    <div className="mb-3">
                      <strong>Status:</strong>
                      <Badge color={editForm.isActive ? 'success' : 'secondary'} className="ml-2">
                        {editForm.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="mb-3">
                      <strong>Source:</strong>
                      <p className="mb-0">
                        {editingExam.questions ? (
                          <Badge color="success">Excel Upload</Badge>
                        ) : (
                          <Badge color="info">{editingExam.selectionMode || 'Legacy'}</Badge>
                        )}
                      </p>
                    </div>
                    <div className="mb-3">
                      <strong>Created:</strong>
                      <p className="mb-0"><small>{formatDate(editingExam.createdAt)}</small></p>
                    </div>

                    {/* Subject breakdown */}
                    {Object.keys(editSubjectCounts).length > 0 && (
                      <div className="mb-3">
                        <strong>Subjects:</strong>
                        <div className="mt-1">
                          {Object.entries(editSubjectCounts)
                            .sort((a, b) => b[1] - a[1])
                            .map(([subject, count]) => (
                              <div key={subject} className="d-flex justify-content-between mb-1">
                                <small>{subject}</small>
                                <Badge color="primary" pill>{count}</Badge>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeEditModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSaveEdit} disabled={updateLoading || isUploadingImages}>
            {isUploadingImages ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Uploading images... {imageUploadProgress}%
              </>
            ) : updateLoading ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save mr-2"></i>
                Save Changes{editQuestions.length > 0 && ` (${editQuestions.length} questions)`}
              </>
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Full Length Exam Upload History */}
      <Container className="mt-4 mb-6" fluid>
        <UploadHistorySection folder="full_length" title="Full Length Exam Upload History" />
      </Container>
    </>
  );
};

export default ManageFullLengthExamsPage;
